/**
 * Badge que mostra o status de operação da organização
 * Indica se está dentro ou fora do horário configurado
 */

import React, { useEffect, useState } from 'react';
import { CheckCircle, Clock, XCircle } from 'lucide-react';
import axios from 'axios';

interface OperatingStatusBadgeProps {
  organizationId: string;
  showDetails?: boolean;
  autoRefresh?: boolean; // Atualizar automaticamente a cada 1 minuto
}

interface OperatingStatus {
  is_operating: boolean;
  current_time: string;
  timezone: string;
  operating_hours: {
    start: string | null;
    end: string | null;
    days: number[];
  };
  organization_name: string;
}

const DAYS_PT = [
  'Dom',
  'Seg',
  'Ter',
  'Qua',
  'Qui',
  'Sex',
  'Sáb'
];

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8090';

export const OperatingStatusBadge: React.FC<OperatingStatusBadgeProps> = ({
  organizationId,
  showDetails = true,
  autoRefresh = true
}) => {
  const [status, setStatus] = useState<OperatingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/organizations/${organizationId}/status`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setStatus(response.data);
    } catch (error) {
      console.error('Erro ao buscar status de operação:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();

    if (autoRefresh) {
      // Atualizar a cada 60 segundos
      const interval = setInterval(fetchStatus, 60000);
      return () => clearInterval(interval);
    }
  }, [organizationId, autoRefresh]);

  if (loading || !status) {
    return null;
  }

  const { is_operating, current_time, operating_hours } = status;

  // Se não tem horário configurado, opera 24/7
  const is247 = !operating_hours.start || !operating_hours.end;

  return (
    <div className="flex items-center gap-2">
      {/* Badge principal de status */}
      <div 
        className="relative"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={() => setShowTooltip(!showTooltip)}
      >
        <div
          className={`
            flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold
            transition-all duration-200 cursor-pointer
            ${
              is247
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : is_operating
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 animate-pulse'
            }
          `}
        >
          {is247 ? (
            <CheckCircle className="w-4 h-4" />
          ) : is_operating ? (
            <CheckCircle className="w-4 h-4" />
          ) : (
            <XCircle className="w-4 h-4" />
          )}
          <span>
            {is247 ? 'Operando 24/7' : is_operating ? 'Em Operação' : 'Fora do Horário'}
          </span>
        </div>

        {/* Tooltip Responsivo */}
        {showTooltip && !is247 && (
          <>
            {/* Overlay para fechar no mobile */}
            <div 
              className="fixed inset-0 z-40 lg:hidden"
              onClick={() => setShowTooltip(false)}
            />
            
            <div className="absolute z-50 left-0 top-full mt-2 lg:left-full lg:top-0 lg:mt-0 lg:ml-2">
              <div className="bg-gray-900 text-white text-xs rounded-lg p-3 shadow-xl border border-gray-700 w-[280px] sm:w-64">
                {/* Título compacto */}
                <div className="font-bold text-sm mb-2 pb-2 border-b border-gray-700 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Horário</span>
                </div>
                
                {/* Informações em grid compacto */}
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-[11px] w-12">Das:</span>
                    <span className="font-semibold text-white text-xs">
                      {operating_hours.start?.slice(0, 5)} às {operating_hours.end?.slice(0, 5)}
                    </span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-gray-400 text-[11px] w-12 pt-0.5">Dias:</span>
                    <div className="flex flex-wrap gap-1">
                      {operating_hours.days.map(d => (
                        <span 
                          key={d}
                          className="px-1.5 py-0.5 bg-gray-800 rounded text-[10px] font-medium"
                        >
                          {DAYS_PT[d]}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                
                {/* Seta do tooltip - desktop only */}
                <div className="hidden lg:block absolute right-full top-3 mr-px">
                  <div className="border-8 border-transparent border-r-gray-900"></div>
                </div>
                
                {/* Seta do tooltip - mobile (topo) */}
                <div className="lg:hidden absolute bottom-full left-4 mb-px">
                  <div className="border-8 border-transparent border-b-gray-900"></div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Horário atual */}
      {showDetails && !is247 && (
        <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm">
          <Clock className="w-3.5 h-3.5" />
          <span className="font-mono">{current_time}</span>
        </div>
      )}

      {/* Mensagem de aviso quando fora do horário */}
      {!is_operating && !is247 && (
        <div className="hidden lg:block text-xs text-amber-600 dark:text-amber-400 italic">
          Os dados exibidos foram registrados durante o horário de operação
        </div>
      )}
    </div>
  );
};

export default OperatingStatusBadge;
